import { useEffect, useMemo, useState } from "react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Trash2, Pencil, Film, RefreshCw, X, Search } from "lucide-react";
import { toast } from "sonner";
import api, { mediaUrl, apiError } from "../../lib/api";
import { accentFor } from "../../lib/accents";
import { AdEditModal } from "../../components/AdEditModal";
import { useConfirm } from "../../components/ConfirmDialog";

const elapsedProcessing = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  return `${h}h${m > 0 ? " " + m + "m" : ""}`;
};

function SortableRow({ ad, categories, onEdit, onDelete, onRemoveCategory, disableDrag = false }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: ad.id, disabled: disableDrag });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1 };
  const cats = (ad.category_ids || []).map((id) => categories.find((c) => c.id === id)).filter(Boolean);

  return (
    <div ref={setNodeRef} style={style} data-testid={`admin-ad-row-${ad.id}`}
      className="flex items-center gap-4 bg-white border border-gray-200 rounded-xl p-3">
      <button {...(disableDrag ? {} : attributes)} {...(disableDrag ? {} : listeners)} data-testid={`drag-handle-${ad.id}`}
        disabled={disableDrag}
        title={disableDrag ? "Drag disabled while filtering — clear filters to reorder" : "Drag to reorder"}
        className={`p-1 ${disableDrag ? "text-gray-200 cursor-not-allowed" : "cursor-grab active:cursor-grabbing text-gray-400 hover:text-[#000000]"}`}>
        <GripVertical size={18} />
      </button>
      <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-100 shrink-0 relative" title={ad.status === "error" ? (ad.error || "Processing failed — please re-upload.") : ""}>
        {ad.status === "ready" ? (
          <img src={mediaUrl(ad.id, "thumb")} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[10px] font-bold text-gray-400">
            {ad.status === "processing" ? "..." : "ERR"}
          </div>
        )}
        {ad.media_type === "video" && (
          <div className="absolute bottom-0.5 right-0.5 bg-black/70 rounded p-0.5"><Film size={10} className="text-white" /></div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-display font-bold text-sm text-[#000000] truncate">{ad.title}</div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {ad.status === "processing" && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#FC861E] text-black" title={`Started ${elapsedProcessing(ad.created_at)} ago`}>
              PROCESSING · {elapsedProcessing(ad.created_at)}
            </span>
          )}
          {ad.status === "error" && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-[#DF1283] text-white" title={ad.error || "Processing failed"}>
              FAILED
            </span>
          )}
          {ad.status === "error" && ad.error && (
            <span className="text-[10px] text-gray-600 italic break-words max-w-full basis-full" data-testid={`admin-ad-error-${ad.id}`}>{ad.error}</span>
          )}
          {cats.slice(0, 3).map((c) => {
            const a = accentFor(c.color);
            return (
              <span key={c.id} data-testid={`admin-ad-tag-${ad.id}-${c.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: a.hex, color: a.text }}>
                {c.name}
                <button type="button" onClick={() => onRemoveCategory(ad, c.id)} data-testid={`remove-tag-${ad.id}-${c.id}`}
                  className="hover:opacity-70" aria-label={`Remove ${c.name}`}
                  style={{ color: a.text }}>
                  <X size={11} strokeWidth={3} />
                </button>
              </span>
            );
          })}
          <span className="text-[11px] text-gray-400">{ad.download_count} downloads</span>
        </div>
      </div>
      <button data-testid={`edit-ad-btn-${ad.id}`} onClick={() => onEdit(ad)}
        className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors"><Pencil size={15} /></button>
      <button data-testid={`delete-ad-btn-${ad.id}`} onClick={() => onDelete(ad)}
        className="p-2 rounded-full hover:bg-[#DF1283] text-gray-500 hover:text-white transition-colors"><Trash2 size={15} /></button>
    </div>
  );
}

export default function AdminAds() {
  const confirm = useConfirm();
  const [ads, setAds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const filteredAds = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ads.filter((a) => {
      if (categoryFilter && !(a.category_ids || []).includes(categoryFilter)) return false;
      if (!q) return true;
      return (
        (a.title || "").toLowerCase().includes(q) ||
        (a.description || "").toLowerCase().includes(q) ||
        (a.hook || "").toLowerCase().includes(q) ||
        (a.angle || "").toLowerCase().includes(q) ||
        (a.offer || "").toLowerCase().includes(q)
      );
    });
  }, [ads, query, categoryFilter]);

  const isFiltered = query.trim() !== "" || categoryFilter !== "";

  const load = () => {
    api.get("/admin/ads").then((r) => setAds(r.data));
    api.get("/admin/categories").then((r) => setCategories(r.data));
  };
  useEffect(() => { load(); }, []);

  // Auto-refresh every 10 seconds while any ad is still processing so
  // the elapsed timer ticks and the status auto-updates when done.
  useEffect(() => {
    const anyProcessing = ads.some((a) => a.status === "processing");
    if (!anyProcessing) return;
    const t = setInterval(() => {
      api.get("/admin/ads").then((r) => setAds(r.data)).catch(() => {});
    }, 10000);
    return () => clearInterval(t);
  }, [ads]);

  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = ads.findIndex((a) => a.id === active.id);
    const newIdx = ads.findIndex((a) => a.id === over.id);
    const next = arrayMove(ads, oldIdx, newIdx);
    setAds(next);
    try {
      await api.post("/admin/ads/reorder", { ordered_ids: next.map((a) => a.id) });
      toast.success("Order saved");
    } catch (e) {
      toast.error("Could not save order");
      load();
    }
  };

  const openEdit = (ad) => setEditing(ad);

  const onSaved = (updated) => {
    setAds((prev) => prev.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
  };

  const remove = async (ad) => {
    if (!(await confirm({
      title: `Delete "${ad.title}"?`,
      message: "The ad, its transcript, and all its uploaded files will be removed. This can't be undone.",
      tone: "danger",
      confirmText: "Delete ad",
    }))) return;
    try {
      await api.delete(`/admin/ads/${ad.id}`);
      setAds((prev) => prev.filter((a) => a.id !== ad.id));
      toast.success("Ad deleted");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeCategory = async (ad, categoryId) => {
    const next = (ad.category_ids || []).filter((c) => c !== categoryId);
    try {
      await api.put(`/admin/ads/${ad.id}`, { category_ids: next });
      setAds((prev) => prev.map((a) => (a.id === ad.id ? { ...a, category_ids: next } : a)));
      toast.success("Tag removed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-black text-3xl text-[#000000]">Ads & Ordering</h1>
          <p className="text-gray-500 mt-1 text-sm">Drag rows to set the display order on the client ad wall.</p>
        </div>
        <button data-testid="refresh-ads-btn" onClick={load}
          className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100 transition-colors">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            data-testid="admin-ads-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title, hook, angle, offer, or description…"
            className="w-full rounded-full border border-gray-300 pl-10 pr-9 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2] focus:border-transparent"
          />
          {query && (
            <button
              data-testid="admin-ads-search-clear"
              onClick={() => setQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#DF1283]"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          data-testid="admin-ads-category-filter"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="rounded-full border border-gray-300 px-4 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {isFiltered && (
        <div className="mt-3 flex items-center justify-between bg-[#0FFFE2] border border-[#0FFFE2] rounded-lg px-4 py-2 text-xs" data-testid="admin-ads-filter-summary">
          <span className="text-gray-600">
            Showing <strong className="text-[#000000]">{filteredAds.length}</strong> of {ads.length} ads.
            <span className="ml-2 text-gray-400">Drag-to-reorder is disabled while filtering.</span>
          </span>
          <button
            data-testid="admin-ads-clear-filters"
            onClick={() => { setQuery(""); setCategoryFilter(""); }}
            className="font-bold text-[#000000] hover:text-[#DF1283] transition-colors"
          >
            Clear filters
          </button>
        </div>
      )}

      <div className="mt-6 space-y-2" data-testid="admin-ads-list">
        {ads.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm" data-testid="admin-ads-empty">No ads yet. Upload your first creative.</div>
        ) : filteredAds.length === 0 ? (
          <div className="text-center py-16 text-gray-400 text-sm" data-testid="admin-ads-no-matches">No ads match your search.</div>
        ) : isFiltered ? (
          filteredAds.map((ad) => (
            <SortableRow key={ad.id} ad={ad} categories={categories} onEdit={openEdit} onDelete={remove} onRemoveCategory={removeCategory} disableDrag />
          ))
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={ads.map((a) => a.id)} strategy={verticalListSortingStrategy}>
              {ads.map((ad) => (
                <SortableRow key={ad.id} ad={ad} categories={categories} onEdit={openEdit} onDelete={remove} onRemoveCategory={removeCategory} />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      <AdEditModal
        ad={editing}
        categories={categories}
        onClose={() => setEditing(null)}
        onSaved={onSaved}
      />
    </div>
  );
}
